import type { Pillar } from './pillars.js';
import { EXTRA_DESTINATIONS } from './navExtraDestinations.js';
import type { Role } from '@attendance/shared';
import type { FeatureFlags, FeatureKey } from '@attendance/shared/features';
/*
 * THE GLYPH SET.
 *
 * Every icon on the rail comes from lucide's OUTLINE set and, since the rail
 * renders them all through one shared size/weight (see `NAV_ICON` in
 * Sidebar.tsx), the only thing left that can make the set look accidental is
 * the drawing itself. Two rules hold here:
 *
 *   1. ONE FAMILY. Lucide ships people glyphs twice — a standard set (`Users`,
 *      `UserCheck`, `CircleUser`) and a "round" set (`UsersRound`,
 *      `CircleUserRound`, exported under the legacy `Users2`/`UserCircle2`
 *      names). They differ in head shape, so a rail that draws from both reads
 *      as two icon sets stacked. Everything here is the standard set.
 *   2. NO TWO DESTINATIONS THAT CAN APPEAR TOGETHER SHARE A GLYPH. Collapsed,
 *      the rail is nothing BUT glyphs — two rows drawn identically are two rows
 *      a user cannot tell apart, and the label that separated them is gone.
 *      Sidebar.test.tsx pins this by comparing the rendered path data, so it
 *      catches two different imports that turn out to be the same picture.
 *
 *      SECTION HEADINGS are outside the rule and repeat their anchor row's
 *      glyph on purpose — Workforce/Employees, Time & pay/Shifts. A heading is
 *      not somewhere you can go, it is the name of what is under it, and even
 *      collapsed it is set apart by the hairline the rail draws above it.
 */
import {
  CalendarDays,
  CircleUser,
  Clock,
  FileSignature,
  FileText,
  Gauge,
  House,
  Megaphone,
  Search,
  Stamp,
  type LucideIcon,
} from 'lucide-react';

export { EXTRA_DESTINATIONS, OPERATIONS };
/* The admin surface's four sections live in their own file (see navGroups.ts)
   and are re-exported here, so `GROUPS` is still imported from `navItems.js`
   by the Sidebar, the command palette, the wiring tests and
   `approvalsGroup.test.ts` exactly as before. */
import { GROUPS, OPERATIONS } from './navGroups.js';
import { featureOn, isVisible, pathMatches } from './navVisibility.js';

/* The predicates over this list live in `navVisibility.ts` — see its header —
   and are re-exported here because that is where every call site imports them
   from. `navLabelOf` is only used through this module too. */
export {
  canSee,
  featureOn,
  navLabelOf,
  isConstructionProfile,
  isVisible,
  pathMatches,
  profileOn,
} from './navVisibility.js';
export { GROUPS };

export interface NavLeaf {
  /** Overrides the group's pillar for a row that belongs to another area. */
  pillar?: Pillar;
  to: string;
  /** i18n key under `nav.*`. */
  labelKey: string;
  /** English fallback. */
  label: string;
  icon: LucideIcon;
  roles?: Role[];
  /**
   * Feature module(s) this item belongs to; hidden when the flag is off. An
   * array requires ALL listed features to be on.
   */
  feature?: FeatureKey | FeatureKey[];
  /**
   * Wording that replaces `labelKey`/`label` while a feature is OFF. This is
   * NOT a second gate: the item stays visible and reachable either way, only
   * its name changes. The one case is the site board, which is the "Site & camp
   * board" only while the camps pillar is on — with camps off the same page is
   * simply the site board, and a nav item named after a module the tenant does
   * not run is the same defect as a screen for it. Reads the same
   * `FeatureFlags` as `feature`, so there is one source of truth for both.
   */
  labelWithout?: { feature: FeatureKey; labelKey: string; label: string };
  /**
   * Hide this item when the tenant runs the construction terminology profile.
   * Used for management actions that don't fit login-less site workers — e.g.
   * the staff "Bulk import" (which needs emails) is wrong for construction,
   * where workers come from the CLI worker/site-master import instead. There's
   * no "import" feature flag, so this gates on the profile directly.
   */
  hideInConstruction?: boolean;
  /**
   * Count rendered as a pill on this row. The rail is where an admin learns
   * there is work waiting — a review queue nobody looks at is a queue that
   * keeps people locked out for a day. Named rather than a number so the nav
   * table stays static data: the Sidebar mounts the matching hook only for the
   * row that asks for one, instead of every leaf running a query.
   *
   * All three approval queues carry one, because they are one kind of thing —
   * rows of people waiting on a decision — and the rail was previously the
   * only surface in the app that counted one of them and not the other two.
   */
  badge?: NavBadgeKind;
  /**
   * A LIVE figure on this row: a number that describes the world right now
   * rather than a queue, drawn with the app's one permitted animation.
   *
   * Declared here rather than special-cased in `Sidebar.tsx` for the reason §2
   * gives for the rest of this file — rail contents come from the nav model, and
   * a number the rail states is rail content. Named, like `badge`, so the query
   * behind it mounts for one row instead of forty. */
  live?: NavLiveKind;
}

/**
 * The counted queues. Each names a hook in `NavBadge.tsx` reading the query key
 * the QUEUE'S OWN SCREEN reads, so a badge counts the rows its queue shows —
 * except `scanApprovals`, whose queue is PAGED and would report its page size
 * as the backlog; it reads a count endpoint applying the identical predicates.
 */
export type NavBadgeKind =
  | 'scanApprovals'
  | 'correctionApprovals'
  | 'leaveApprovals'
  | 'letterApprovals'
  | 'deviceApprovals'
  | 'erpChanges';

/** The live figures. One, so far: how many people are on a worksite now. */
export type NavLiveKind = 'onSiteNow';

export interface NavGroup {
  id: string;
  titleKey: string;
  title: string;
  icon: LucideIcon;
  items: NavLeaf[];
  /** Which pillar accent this group's rows carry. See `Pillar` below. */
  pillar?: Pillar;
}


/**
 * The single most important action — elevated as a primary CTA everywhere.
 *
 * It lands on HOME, not on a separate page: the check-in panel is the top of
 * the personal dashboard now, so the standalone /punch route is gone. The CTA
 * stays because "check in" is what people are looking for in the rail — naming
 * that destination "Home" would hide the app's main verb behind a place.
 */
export const PRIMARY: NavLeaf = {
  to: '/',
  labelKey: 'nav.punch',
  label: 'Check in / out',
  icon: Stamp,
  // Personal self-punch. Hidden when the tenant is a management-only console
  // (login-less site workers punch via the field APK/kiosk instead).
  feature: 'selfService',
};

/**
 * "Me" — the personal workspace every user (admins included) lands on. Home is
 * the personal dashboard and is always shown; the rest are self-service items.
 * Corrections lives here too: it's the worker's own "request a fix to my
 * punches" page (the approver's queue is the same route, role-adapted), so it
 * belongs to the personal side, not the Admin panel.
 * Notifications lives only in the top-bar bell (and the mobile "Alerts" tab).
 */
export const PERSONAL: NavLeaf[] = [
  // `House`, not `LayoutDashboard`. This row is labelled "Home" and it lands on
  // the worker's own screen; the grid glyph is what the app draws for a
  // dashboard, so the picture and the word disagreed. It is also the drawing the
  // mobile bottom bar's home tab already uses — lucide's `Home` is an alias of
  // `House`, so the two surfaces are now one picture rather than two.
  { to: '/', labelKey: 'nav.home', label: 'Home', icon: House },
  { to: '/my-attendance', labelKey: 'nav.myAttendance', label: 'My attendance', icon: Clock, feature: 'selfService' },
  { to: '/corrections', labelKey: 'nav.corrections', label: 'Corrections', icon: FileSignature, feature: 'corrections' },
  { to: '/leave', labelKey: 'nav.myLeave', label: 'My leave', icon: CalendarDays, feature: 'leave' },
  { to: '/announcements', labelKey: 'nav.announcements', label: 'Announcements', icon: Megaphone, feature: 'announcements' },
  // A letter is a document, not a workbook — this row carried `FileSpreadsheet`,
  // which is the glyph the app uses for an Excel export everywhere else.
  { to: '/letters', labelKey: 'nav.letters', label: 'Letters', icon: FileText, feature: 'letters' },
  { to: '/profile', labelKey: 'nav.myProfile', label: 'My profile', icon: CircleUser, feature: 'selfService' },
];

/**
 * The heading over the personal list — the one section title on this side of
 * the rail.
 *
 * The admin surface names every one of its sections and the personal surface
 * named none, so on a COMBINED console (self-service off is not the only way to
 * get one — see `mode` in Sidebar.tsx) the personal rows and the first admin
 * group ran together under a single hairline with nothing saying which was
 * which. The handoff draws a heading here for that reason.
 *
 * IT REUSES `nav.myWorkspace`, and that is the whole point of not adding a key:
 * a rail heading with its own wording for the same surface would be the app
 * calling one place two things.
 *
 * THE ENGLISH VALUE WAS "Me" AND THE ARABIC WAS "مساحة عملي" — My workspace.
 * One key, one surface, two names, and the surface's other appearance — the
 * switch back from the Admin panel — said "Back to my workspace" in both. So
 * the product called this place "Me" in exactly one spot, in exactly one
 * language, and §5's "one name per thing" was being broken by the locale file
 * rather than by the code. The English is now "My workspace" too: it is the
 * name the key already had, the name the other three places already used, and
 * the name a reader has already met on the way here. The handoff's own label
 * is "Me"; that is what the mock is, and §0 puts consistent product vocabulary
 * above it. `label` below is the untranslated fallback and follows.
 *
 * The Arabic switch label moved with it, from "العودة إلى مساحتي" to
 * "العودة إلى مساحة عملي", for the same reason in the other direction: Arabic
 * had the two spellings the English pair did not.
 *
 * Items are NOT held here: the personal list is filtered per user (feature
 * flags, and whether the login has an Employee record behind it at all), so
 * this carries only the naming. `Omit<NavGroup, 'items'>` rather than a fourth
 * shape, so a heading is the same kind of thing on both surfaces.
 *
 * `CircleUser` repeats the "My profile" row's glyph, under the heading rule at
 * the top of this file — a heading is the name of what is under it, not a
 * destination, so it is outside the one-picture-one-destination rule.
 */
export const PERSONAL_SECTION: Omit<NavGroup, 'items'> = {
  id: 'me',
  titleKey: 'nav.myWorkspace',
  title: 'My workspace',
  icon: CircleUser,
};

/**
 * Landing page of the Admin panel (the management surface). Everyone — admins
 * included — lands on their personal workspace by default; opening the Admin
 * panel brings them here, the workforce/management overview. It sits above the
 * back-office GROUPS in the admin sidebar.
 */
export const ADMIN_HOME = '/overview';

export const OVERVIEW: NavLeaf = {
  /* The admin home takes the BRAND, which is also the resolver's fallback — so
     this tag changes nothing visually and everything about the contract: an
     untagged top-level destination is a silent regression to "everything is
     brand", and `pillarAccent.test.tsx` can only catch that if every area
     states its pillar rather than relying on the default. */
  pillar: 'people',
  to: ADMIN_HOME,
  labelKey: 'nav.overview',
  label: 'Overview',
  icon: Gauge,
  roles: ['ADMIN', 'HR', 'MANAGER'],
};

/**
 * The control room — today's floor, in one screen.
 *
 * It used to be a GROUP of three: "Live attendance", "Site & camp board" and
 * "Live site board". They answered overlapping halves of one question and
 * disagreed in the gaps, so they are now one page with four views, which leaves
 * the group holding a single item named the same thing as the group it sits in
 * — the exact defect the Reports note below describes. So it is promoted to a
 * top-level management destination beside Overview instead: two rows, the
 * summary and the floor, then the back-office sections underneath.
 *
 * NOT feature-gated. The site half needs the `workers` pillar and the page
 * hides it accordingly, but the people half (who is on the clock, who is
 * flagged) applies to every tenant — and one of the pages this replaces was
 * reachable with no feature gate at all.
 */
/* OPERATIONS MOVED TO THE ATTENDANCE GROUP (navGroups.ts) — it is the sibling
 * of the month, not a peer of the headings. Its live `onSiteNow` count and its
 * `sites` pillar travelled with it; `WIDE_ROUTES` below now names its path as a
 * literal, like the calendar beside it, because navGroups cannot be imported
 * from here without a runtime cycle. */

/**
 * The month, for the whole workforce. Promoted for the same reason as
 * Operations: it is a screen you open to look at the company, it owns its own
 * filters (group, department, month), and it is the one management destination
 * a tenant with no leave, shifts or payroll module still has — under the lean
 * preset it was the ONLY survivor of its group, i.e. a collapsible section you
 * expanded to find one link.
 *
 * Not feature-gated, like Operations: attendance is what this app is.
 */
/* MOVED TO THE ATTENDANCE GROUP (navGroups.ts).
 *
 * Both were promoted to top-level rows because a group of theirs collapsed to a
 * single link on a lean tenant — "the ONLY survivor of its group, i.e. a
 * collapsible section you expanded to find one link". That hazard is handled
 * generically now: `NavGroupSection` renders any one-item group as a plain row,
 * for every group and every feature combination at once. So these two can sit
 * with the rest of attendance, which is where a reader looks for them, without
 * re-creating the defect that moved them out.
 */

/**
 * REPORTS IS ONE DESTINATION, at the top level, beside the other three.
 *
 * It has been a leaf, then a two-row section, and it is a leaf again — so the
 * reason matters more than usual. A sidebar section earns its heading by
 * holding rows a reader has to CHOOSE BETWEEN. Reports stopped being that when
 * the section collapsed onto one screen: `/reports` is a search bar over the
 * tenant's own figures, every report is a question it composes, and the two
 * that are still screens (the attendance workspace, the monthly timesheet) are
 * offered inside it by name — through the same catalog that gates them on role
 * and feature, so the bar cannot offer what the rail would have hidden.
 *
 * That left a heading, a chevron, and a row called "Smart report" nested under
 * a heading called "Reports" — the reader paying a click to be told the section
 * is the screen. And "Smart report" named the CONTROL rather than the
 * destination: nobody in the office goes looking for a smart report, they go
 * looking for reports.
 *
 * It sits at the top level rather than in a section because it is the same
 * shape as the three beside it — a screen you open to look at the company, that
 * owns its own controls and its own way in. Overview, Operations, Attendance
 * calendar, Reports: the summary, today's floor, the month, and the question.
 *
 * `Search`, not `Sparkles`. There is no model in it and nothing is generated; a
 * magic glyph would promise one. Same reason the Administration group rejected
 * the same icon.
 */
export const REPORTS: NavLeaf = {
  pillar: 'reports',
  to: '/reports',
  labelKey: 'nav.reports',
  label: 'Reports',
  icon: Search,
  roles: ['ADMIN', 'HR', 'MANAGER'],
  feature: 'reports',
};

/**
 * THE ATTENDANCE WORKSPACE, BACK ON THE RAIL.
 *
 * It had no row of its own, on the reasoning recorded above: Reports offers it
 * by name inside the bar, so a rail row would be a second way in to something
 * already listed one row up. That reasoning holds for a reader who knows the
 * screen exists and knows the bar will offer it. It fails for everyone else,
 * and it failed in practice — the workspace is where the office actually
 * works (filter once; KPIs, charts and one table of hours and overtime you can
 * sort, narrow and export), and taking its row away made a daily destination
 * something you had to remember the name of before the app would admit to it.
 *
 * Findability beats tidiness here. `/reports` answers a QUESTION and hands back
 * one figure with its working; this hands back the whole table. They are two
 * different jobs, which is exactly the test a rail row is supposed to pass —
 * rows the reader has to choose between — so both get one.
 *
 * `Table2`, not another magnifier: the difference between the two rows IS that
 * one is a search and the other is a table, and the glyphs should say so at a
 * glance rather than making the label do all the work.
 */


/**
 * The management destinations that stand on their own, in the order the office
 * reaches for them: the summary, today's floor, the month, the question.
 *
 * Each one owns an internal navigator — Operations has four views, the
 * attendance calendar its filters, Reports its search bar — which is precisely
 * why none of them belongs inside a collapsible list of siblings. What they
 * have in common is the test for this list: you open it to look at the company,
 * not to act on one record.
 */
/**
 * THE ROSTER, and the one record LIST with a top-level row.
 *
 * It bends this list's own test — "you open it to look at the company, not to
 * act on one record" — deliberately. Employees is the app's most-opened screen
 * (`navItems.test.ts` says so where it explains why bulk import is not beside
 * it) and it was sitting fourth inside a collapsible group. A rail that files
 * its busiest destination under a heading is ordering rows by taxonomy rather
 * than by use.
 *
 * `pillar` is stated rather than inherited: it used to come from the Workforce
 * group. `pillarForPath` would fall back to `people` anyway, but an accent
 * resting on a fallback moves the day the fallback does.
 */
/* EMPLOYEES MOVED TO THE PEOPLE & PAY GROUP (navGroups.ts). It was promoted
 * here so the app's busiest screen would not sit "fourth inside a collapsible
 * group" — but the group it sat outside was the one named after it, so a reader
 * hunting the roster opened People and did not find it. Groups render expanded,
 * so the row is on screen either way; it is now FIRST in that group. */

/**
 * THE WHOLE-COMPANY SCREENS THAT STAND ALONE.
 *
 * The rule: a LEAF is a whole-company screen with no siblings to choose
 * between; anything with siblings is a GROUP row. Overview is the summary,
 * Operations is today's floor (see above) and Reports is the archive.
 *
 * Employees used to be here on a different test — "owns an internal navigator
 * of its own" — which never separated it from the rows beside it: the
 * attendance calendar owns its filters too. It is a group row now
 * (navGroups.ts), first inside the group named after it.
 */
export const TOP_LEVEL: NavLeaf[] = [OVERVIEW, OPERATIONS, REPORTS];


/**
 * Routes that take the FULL width of the shell instead of the reading cap.
 *
 * The cap (`max-w-content`) is right for a form, a table and a settings page,
 * and wrong for a wall display: a map capped at the text measure with a metre
 * of background either side of it is not a control room. Declared here, beside
 * the rest of the route knowledge, so the exception lives in one place rather
 * than each page fighting the wrapper with negative margins.
 *
 * THE ATTENDANCE CALENDAR IS THE SECOND MEMBER, and for the same reason rather
 * than a new one. Its grid is one column PER DAY — up to 31 of them beside the
 * employee column and the P/A total — so its natural width is a function of the
 * month, not of a reading measure. Capped at 1600px the day columns compress
 * until the cells stop being scannable, which is the whole job of the screen;
 * the cap does not make it easier to read, it makes it narrower. Everything
 * else about the page (its measure, its cards, its copy) still answers to the
 * ordinary rules.
 */
/* The calendar's path is spelt out rather than read off its leaf: that leaf
   lives in the Attendance group now (navGroups.ts), and navItems imports GROUPS
   from there — reaching back the other way for one string would close an
   import cycle for no gain. `navItems.test.ts` asserts the two agree. */
const WIDE_ROUTES: readonly string[] = ['/operations', '/attendance-calendar'];

export function isWideRoute(pathname: string): boolean {
  return WIDE_ROUTES.some((to) => pathMatches(to, pathname));
}

/**
 * Does this user have ANY management access — i.e. is there at least one
 * back-office item their role + features let them see? Drives whether the
 * "Admin panel" switch appears at all. A rank-and-file employee gets `false`
 * and never sees the management surface.
 */
export function hasAdminAccess(
  role: Role | undefined,
  features: FeatureFlags | undefined
): boolean {
  // Only role-restricted (management) items count. An ungated back-office item
  // would otherwise grant every employee "admin access"; such items belong to
  // the personal workspace instead.
  return (
    TOP_LEVEL.some((i) => !!i.roles && isVisible(i, role, features)) ||
    GROUPS.some((g) => g.items.some((i) => !!i.roles && isVisible(i, role, features)))
  );
}

/**
 * Is `pathname` part of the Admin panel (management) surface? True for the
 * Overview landing and any back-office group route (incl. sub-paths). Personal
 * routes — home, punch, my-attendance, my leave, announcements, letters,
 * profile, notifications — are NOT admin paths, so they read as the personal
 * workspace. This is the single source of truth for which surface the shell is
 * showing, derived straight from the nav config so the two never drift.
 */
export function isAdminPath(pathname: string): boolean {
  // Role-gated items only — an ungated back-office route (e.g. Corrections) is
  // part of the personal workspace, so visiting it must NOT flip to admin mode.
  // Reading TOP_LEVEL rather than naming Overview and Operations by hand is
  // what keeps a newly promoted destination from silently dropping the reader
  // into the personal workspace on a back-office screen.
  const claims = (i: NavLeaf): boolean => !!i.roles && pathMatches(i.to, pathname);
  // EXTRA_DESTINATIONS COUNTS TOO, and leaving it out was a latent bug rather
  // than a simplification: a role-gated page is an admin page whether or not it
  // earned a rail row, and this list is exactly the pages that are reachable
  // without one. It went unnoticed while every entry was either ungated
  // (Notifications) or a query-string view of a listed row (the people map,
  // whose bare path `/operations` is claimed by TOP_LEVEL anyway). The leave
  // calendar is the first entry that is a real, role-gated path of its own —
  // without this line, an admin who opened it would have been dropped into the
  // personal workspace on a back-office screen.
  return (
    TOP_LEVEL.some(claims) ||
    GROUPS.some((g) => g.items.some(claims)) ||
    EXTRA_DESTINATIONS.some(claims)
  );
}

/** Every destination the role can reach, flattened — for the command palette. */
export function allDestinations(
  role: Role | undefined,
  features?: FeatureFlags
): NavLeaf[] {
  const primary = featureOn(PRIMARY, features) ? [PRIMARY] : [];
  const personal = PERSONAL.filter((i) => featureOn(i, features));
  const top = TOP_LEVEL.filter((i) => isVisible(i, role, features));
  const groupItems = GROUPS.flatMap((g) => g.items).filter((i) => isVisible(i, role, features));
  const extras = EXTRA_DESTINATIONS.filter((i) => isVisible(i, role, features));
  /*
   * DE-DUPED BY DESTINATION.
   *
   * The check-in CTA and the personal "Home" row are both `/` — the sidebar
   * knows that and drops one (it renders the CTA instead), but this list did
   * not, so the command palette offered two rows that went to the same screen
   * and React saw a duplicate key, because the palette keys on `to`.
   *
   * Keeping the FIRST occurrence keeps the CTA's wording ("Check in / out"),
   * which is what somebody typing into the palette is actually looking for —
   * naming that destination "Home" would hide the app's main verb behind a
   * place.
   */
  const seen = new Set<string>();
  return [...primary, ...personal, ...top, ...groupItems, ...extras].filter((i) => {
    if (seen.has(i.to)) return false;
    seen.add(i.to);
    return true;
  });
}
